// @flow
import stringify from 'json-stringify-safe'
import Circle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Icon from 'ol/style/Icon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import Text from 'ol/style/Text'
import type { MapsourcesType } from './reducers/mapsources'

export const fetchKnownBoundaries = (boundary, boundaryType, onSuccess, onError) => {
    let fetchError = false
    const payload = {
        extent: boundary,
        boundaryType,
    }
    fetch('/er2_geoprocessing/api/v1/known-boundaries/', {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: stringify(payload),
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        if (fetchError) {
            onError(json)
        } else {
            const valueField = boundaryType
            onSuccess(json.items.map(item => ({ ...item, value: item[valueField] || item.name })))
        }
    })
}

export const fetchKnownBoundary = (boundaryId, boundaryType, onSuccess, onError) => {
    let fetchError = false
    fetch(`/er2_geoprocessing/api/v1/known-boundaries/${boundaryType}/${boundaryId}`)
        .then((response) => {
            fetchError = !response.ok
            return response.json()
        }).then((json) => {
            if (fetchError) {
                onError(json)
            } else {
                onSuccess(json.geojson)
            }
        })
}

// Return the mapsource for user layers
export const getMapserver = (mapsources: MapsourcesType) => mapsources.mapsources.find(mapsource => mapsource.name === 'default')

/**
 * Get the flat list of layers
 * @param mapsources
 */
export const getLayers = (mapsources: MapsourcesType) => {
    let ret = []
    mapsources.mapsources.forEach((mapsource) => {
        if (mapsource.layers) ret = [...ret, ...mapsource.layers]
    })
    return ret
}

/**
 * Get the associated layer object
 * @param mapsources
 * @param mapsourceName
 * @param layerName
 */
export const getLayer = (mapsources: MapsourcesType, mapsourceName: string, layerName: string) => {
    let ret = null
    mapsources.mapsources.forEach((mapsource) => {
        if (mapsource.name === mapsourceName) {
            if (mapsource.type === 'arcgisrest') {
                ret = mapsource
            } else if (mapsource.type === 'mapserver') {
                const layer = mapsource.layers.find(l => l.mapsource === mapsourceName && l.name === layerName)
                if (layer) ret = layer
            }
        }
    })
    return ret
}

export const getLayerType = (layerType: string) => {
    const layerConversions = {
        polygon: 'poly',
        multipolygon: 'poly',
        line: 'line',
        linestring: 'line',
        multilinestring: 'line',
        point: 'point',
        multipoint: 'point',
        raster: 'raster',
    }
    return layerConversions[layerType.toLowerCase()]
}

/**
 * Return the mapserver host location
 * @returns {string}
 */
export const getMapserverHost = () => {
    let server = window.location.origin
    // Check for django dev server, in which case mapserver requests just go to port 80
    if (window.location.port >= 8000) {
        server = `${window.location.protocol}//${window.location.hostname}`
    }
    return server
}

export const mapToolbarHasActivePanels = (mapToolbar) => {
    const activePanels = mapToolbar.items.filter(item => item.active)
    return activePanels.length > 0
}

export const getSymbolStyle = (layer, classification) => {
    const patternShape = getLayerType(layer.layer_type)
    const { borderColor, color, symbolType, transparent } = classification
    if ((symbolType === 'Solid' || patternShape !== 'point')) {
        return {
            backgroundColor: transparent === true ? 'initial' : color,
            borderColor,
        }
    }
    return {}
}

export const getSymbolURL = (layer, classification) => {
    const patternShape = getLayerType(layer.layer_type)
    const icons = layer.icons
    const { symbolType, symbol } = classification
    return `${icons.url}${patternShape}/${symbolType || 'Solid'}/${symbol || 'Solid.png'}`
}

/**
 *
 * @param feature Openlayers feature
 * @param layer catena layer with classifications
 */
export function getFeatureStyleFromLayer(feature, layer) {
    // Parse label
    let text
    if (layer.labelsVisible) {
        const backgroundFill = layer.labelBackgroundVisible? new Fill({ color: layer.labelBackgroundColor }) : undefined
        text = new Text({
            backgroundFill,
            font: `${layer.labelSize}px "${layer.labelFont}", sans-serif`,
            fill: new Fill({ color: layer.labelTextColor }),
            offsetX: '0',
            // TODO: change this to be a function of symbol size
            offsetY: '30',
            stroke: new Stroke({ color: layer.labelBorderColor, width: 2 }),
            text: feature.get(layer.labelField),
        })
    }

    let style
    if (layer.layer_type === 'Point') {
        style = new Style({
            fill: new Fill({
                color: 'rgba(11, 78, 41, 0.7)',
                opacity: 0.6,
            }),
            image: new Circle({
                fill: new Fill({
                    color: 'rgba(51, 204, 255,0.3)',
                }),
                stroke: new Stroke({
                    width: 1,
                    color: 'blue',
                }),
                radius: 7,
            }),
            stroke: new Stroke({
                color: 'green',
                width: 3,
            }),
            text,
        })
    } else if (layer.layer_type.includes('Polygon')) {
        style = new Style({
            stroke: new Stroke({
                color: 'green',
                width: 3,
            }),
            text,
        })
    }

    // Parse classifications
    const classifications = layer.classifications
    classifications.forEach((c) => {
        if (c.expression) {
            console.warn('Handle expression')
        } else {
            // Single symbol
            if (c.symbol) {
                // Found a symbol, convert this to an openlayers image style
                style = new Style({
                    image: new Icon({
                        scale: c.size / 20,
                        src: getSymbolURL(layer, c),
                    }),
                    text,
                })
            } else {
                style = new Style({
                    stroke: new Stroke({
                        color: c.bordercolor,
                        width: c.bordersize,
                    }),
                    text,
                })
            }
        }
    })
    return style
}
